import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { colors } from '../../../../colors';
import { ComponentDimensions } from '../../../../types/Layout';
import useCPCDaemonQueueWebsocket from '../../../../hooks/useCPCDaemonQueueWebsocket';
import useCPCProgressWebsocket, {
  EMPTY_PROGRESS,
} from '../../../../hooks/useCPCProgressWebsocket';
import useRequest from '../../../../hooks/useRequest';
import ServicePaths from '../../../../bridge/utils/ServicePaths';
import SpcResultView from '../../../Subscription/Content/Calculations/SpcResultView';
import ProfitDistributionChart from '../CalculationResultHistory/ProfitDistributionChart';
import {
  Bar,
  Panel,
  QueueListItem,
  StatsBlock,
  InterruptedBanner,
  GlobalBestCard,
  Spinner,
  formatDurationCompact,
} from './shared';
import { computeComboETAMs, computeOverallETAMs } from './eta';

const MOBILE_MAX_WIDTH = 700;
const TABLET_MAX_WIDTH = 1100;

const reorderQueueOnServer = (botFriendlyName: string): void => {
  fetch(ServicePaths.DATA.ReorderDaemonQueue, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ botFriendlyName }),
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Failed to reorder daemon queue', err);
  });
};

interface Props {
  rootDimensions?: ComponentDimensions | undefined;
}

const LiveCPCDaemon = ({ rootDimensions }: Props) => {
  const queue = useCPCDaemonQueueWebsocket();
  const { runsByAsset } = useCPCProgressWebsocket();
  const [now, setNow] = useState(Date.now());
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Which assets can be viewed: the ones actively running (from the daemon
  // queue) plus any with a (possibly finished) run we've received. The user
  // clicks an active queue item to choose which run's live results to view; we
  // default to the first active asset until they pick one.
  const queueItems = queue.queueState?.queue ?? [];
  const activeAssets = queueItems
    .filter((q) => q.active)
    .map((q) => q.botFriendlyName);
  const viewableAssets = Array.from(
    new Set([...activeAssets, ...Object.keys(runsByAsset)]),
  );
  const selected =
    selectedAsset && viewableAssets.includes(selectedAsset)
      ? selectedAsset
      : activeAssets[0] ?? viewableAssets[0] ?? null;
  const [selBase, selQuote] = selected ? selected.split('-') : [null, null];
  // Fall back to an asset-labelled placeholder for an active asset that hasn't
  // streamed its first message yet, so the layout shows immediately.
  const progress =
    (selected ? runsByAsset[selected] : undefined) ??
    ({
      ...EMPTY_PROGRESS,
      baseAsset: selBase ?? null,
      quoteAsset: selQuote ?? null,
    } as typeof EMPTY_PROGRESS);
  const isIdle = viewableAssets.length === 0;

  // The parent measures `rootDimensions` once at mount and never updates it on
  // resize, so we observe our own root element to drive responsive breakpoints.
  // The dep on isIdle re-binds the observer after the early-return path swaps
  // out for the full layout, when rootRef.current finally points at the real
  // root.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setMeasuredWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isIdle]);

  const comboETA =
    progress.phase && progress.currentCombo
      ? computeComboETAMs({
          nowMs: now,
          comboStartedAt: progress.currentCombo.startedAt,
          phase: progress.phase,
          phaseDone: progress.phaseDone,
          phaseTotal: progress.phaseTotalIterations,
          passIndex: progress.phasePass,
        })
      : null;

  const fallbackTotalDurationMs =
    queueItems.find((q) => q.botFriendlyName === selected)?.durationCPC ?? null;

  const overallETA = computeOverallETAMs({
    comboIndex: progress.comboIndex,
    comboTotal: progress.comboTotal,
    finishedDurations: progress.finishedDurations,
    fallbackTotalDurationMs,
    currentComboETAMs: comboETA,
  });

  const spcParams = useMemo(() => {
    const best = progress.bestGlobal;
    if (!best) return undefined;
    return {
      baseAsset: best.params.baseAsset,
      quoteAsset: best.params.quoteAsset,
      exchange: best.params.exchange,
      startTimestamp: new Date(best.params.startTimestamp).valueOf(),
      endTimestamp: Date.now(),
      higherTimeFrame: best.params.higherTimeFrame,
      shorterTimeFrame: best.params.shorterTimeFrame,
      multiOrderTimeFrame: best.params.multiOrderTimeFrame,
      cbrt: best.params.cbrt,
      shorterTimeFrameInterval: best.params.shorterTimeFrameInterval,
      useTakeProfitTransactions: !!best.indicatorConfig,
      indicatorConfig: best.indicatorConfig,
      // Forward the winner's RSI-MRC config so the live SPC reflects it (without
      // this the SPC re-runs with no MRC and the config view shows blanks).
      mrcRsiConfig: best.mrcRsiConfig ?? null,
    };
  }, [progress.bestGlobal]);

  const spcOptions = useMemo(
    () => ({ method: 'POST' as const, shouldFetch: !!spcParams }),
    [spcParams],
  );

  const { data: spcResult, isValidating: spcLoading } = useRequest(
    ServicePaths.PROFIT_CALCULATIONS.GetSingleProfitCalculation,
    spcParams,
    spcOptions,
  );

  const handleReorder = useCallback((botFriendlyName: string) => {
    reorderQueueOnServer(botFriendlyName);
  }, []);

  if (isIdle) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          height: '100%',
          color: colors.gray,
        }}
      >
        Daemon idle — trigger a CPC via <code>POST /calculations/add</code> to
        see live updates.
      </div>
    );
  }

  const overallPct =
    progress.comboTotal === 0
      ? 0
      : (progress.comboIndex / progress.comboTotal) * 100;
  const comboPct =
    progress.phaseTotalIterations === 0
      ? 0
      : (progress.phaseDone / progress.phaseTotalIterations) * 100;
  const phaseDurationMs = progress.phaseStartedAt
    ? now - progress.phaseStartedAt
    : null;
  const totalDurationMs = progress.startedAt ? now - progress.startedAt : null;

  const viewportWidth = measuredWidth ?? rootDimensions?.width ?? 1200;
  const isMobile = viewportWidth < MOBILE_MAX_WIDTH;
  const isTablet = !isMobile && viewportWidth < TABLET_MAX_WIDTH;

  const gridTemplateColumns = isMobile
    ? '1fr'
    : isTablet
    ? '1fr 1fr'
    : '220px 260px 1fr 320px';

  // Active items (running CPCs, listed first) are clickable to view that run's
  // live results. The first pending item is already next-up so it's inert; the
  // rest can be promoted to next-up — see daemons/cpc.ts moveBotToNextInQueue.
  const activeCount = activeAssets.length;
  const queueScrollMaxHeight = isMobile ? 320 : isTablet ? 360 : undefined;

  // Per-asset overall progress, for the % shown beside each running item.
  const pctForAsset = (key: string): string | undefined => {
    const run = runsByAsset[key];
    if (!run) return undefined;
    if (run.isFinished) return '100%';
    const pct =
      run.comboTotal === 0 ? 0 : (run.comboIndex / run.comboTotal) * 100;
    return `${pct.toFixed(1)}%`;
  };

  const renderQueueScroll = () => (
    <div
      className="live-cpc-queue-scroll"
      style={
        queueScrollMaxHeight
          ? { maxHeight: queueScrollMaxHeight }
          : { flex: 1, minHeight: 0 }
      }
    >
      {queueItems.map((q, idx) => (
        <QueueListItem
          key={q.botFriendlyName}
          item={q}
          variant="list"
          selected={q.botFriendlyName === selected}
          onClick={
            q.active
              ? () => setSelectedAsset(q.botFriendlyName)
              : idx > activeCount
              ? () => handleReorder(q.botFriendlyName)
              : undefined
          }
          rightLabel={q.active ? pctForAsset(q.botFriendlyName) : undefined}
        />
      ))}
    </div>
  );

  // On desktop/tablet the queue sits beside the heatmap; we use the absolute
  // positioning trick so its height is governed by the (taller) heatmap and
  // not by the full queue list. On mobile the queue is its own row, so we
  // give it an explicit max-height to keep it scrollable.
  const queueCell = isMobile ? (
    <Panel title="Queue">{renderQueueScroll()}</Panel>
  ) : (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <Panel
        title="Queue"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderQueueScroll()}
      </Panel>
    </div>
  );

  return (
    <div
      ref={rootRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        minHeight: '100%',
      }}
    >
      <Panel>
        {progress.isFinished && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(0, 176, 97, 0.18)',
              border: `1px solid ${colors.green}`,
              color: colors.white,
              fontSize: 14,
              marginBottom: 12,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <span>
              <strong style={{ color: colors.green }}>✓ Run finished</strong>
              {selected && ` — ${selected}`}
            </span>
            <span style={{ opacity: 0.8 }}>
              took {formatDurationCompact(progress.finishedDuration ?? 0)}
            </span>
          </div>
        )}
        <Bar
          pct={progress.isFinished ? 100 : overallPct}
          label={`Overall · ${progress.comboIndex}/${
            progress.comboTotal
          } combos · ${selected ?? '—'} · ETA ${
            progress.isFinished ? '—' : formatDurationCompact(overallETA ?? 0)
          }`}
        />
        <div style={{ height: 8 }} />
        <Bar
          pct={progress.isFinished ? 100 : comboPct}
          label={`Current combo · ${progress.phaseDone}/${
            progress.phaseTotalIterations
          } · ETA ${
            progress.isFinished ? '—' : formatDurationCompact(comboETA ?? 0)
          }`}
        />
        {progress.interruptedReason && (
          <div style={{ marginTop: 10 }}>
            <InterruptedBanner reason={progress.interruptedReason} />
          </div>
        )}
      </Panel>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns,
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        {queueCell}
        <Panel title="Stats">
          <StatsBlock
            input={{
              botFriendlyName: selected,
              phase: progress.phase,
              phasePass: progress.phasePass,
              phaseDone: progress.phaseDone,
              phaseTotal: progress.phaseTotalIterations,
              comboIndex: progress.comboIndex,
              comboTotal: progress.comboTotal,
              currentCombo: progress.currentCombo,
              comboETA,
              phaseDurationMs,
              totalDurationMs,
            }}
          />
        </Panel>
        <Panel title="Heatmap">
          {progress.overview ? (
            <ProfitDistributionChart
              data={progress.overview}
              setTimeFrameCombination={() => undefined}
            />
          ) : (
            <div style={{ opacity: 0.6 }}>Waiting for first combo result…</div>
          )}
        </Panel>
        <Panel title="Global best">
          <GlobalBestCard result={progress.bestGlobal} />
        </Panel>
      </div>

      <Panel
        title={
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            Best result — live SPC
            {spcLoading && <Spinner />}
          </span>
        }
      >
        {progress.bestGlobal && spcResult ? (
          <SpcResultView key={progress.runId ?? 'no-run'} data={spcResult} />
        ) : (
          <div style={{ opacity: 0.6, padding: 8 }}>
            {progress.bestGlobal
              ? 'Loading SPC for current best…'
              : 'Waiting for first best result…'}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default LiveCPCDaemon;
